/**
 * Dates as a person would say them.
 *
 * An administrator reading a list of accounts wants to know which are alive,
 * and "2026-09-01T04:12:55.913Z" does not answer that at a glance. Anything
 * inside the last month is said in days or weeks; past that the exact date is
 * more use than "3 months ago", because by then you are checking a record
 * rather than a habit.
 */
const DAY = 86_400_000;

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  // Day first, and spelled the way the rest of the interface speaks: this is a
  // British-English product, and "5 Sep 2026" beside "organisation" reads as
  // one voice where "Sep 5, 2026" does not.
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function timeAgo(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return 'never';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return 'never';

  // Whole days apart by the calendar, not by the clock: something at 11pm
  // last night was "yesterday", not "9 hours ago".
  const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((midnight(now) - midnight(then)) / DAY);

  if (days < 0) return formatDate(iso);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 28) {
    const weeks = Math.floor(days / 7);
    return weeks === 1 ? 'last week' : `${weeks} weeks ago`;
  }
  return formatDate(iso);
}

/**
 * When something happened, for a log: the day as a person says it, and the
 * time, because in a record of who did what the order within a day matters.
 *
 * "today" alone on every row of a busy afternoon told an owner nothing about
 * which change came first — the thing they had opened the log to find out.
 */
export function eventTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '—';

  const clock = then.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((midnight(now) - midnight(then)) / DAY);

  if (days === 0) return `Today, ${clock}`;
  if (days === 1) return `Yesterday, ${clock}`;
  if (days > 1 && days < 7) {
    return `${then.toLocaleDateString('en-GB', { weekday: 'long' })}, ${clock}`;
  }
  const sameYear = then.getFullYear() === now.getFullYear();
  const date = then.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  return `${date}, ${clock}`;
}

/** The initials on a record card. One letter, or two where there are two names. */
export function initials(fullName: string): string {
  const parts = fullName
    .trim()
    .split(/\s+/)
    .filter((p) => /\p{L}/u.test(p));
  if (parts.length === 0) return '—';
  // The first letter of a word, not its first character: «Лидер» is Л, not «.
  const letterOf = (word: string) => word.match(/\p{L}/u)?.[0] ?? '';
  const first = letterOf(parts[0]!);
  const last = parts.length > 1 ? letterOf(parts[parts.length - 1]!) : '';
  return (first + last).toUpperCase();
}
